import { Connection } from "./comparables/Comparables";

export type ZooplaPropertyType = "Other" | "Terraced" | "Semi-Detached" | "Detached" | "Flat/Maisonette";

export interface ZooplaComparableSearchResponseMetaData {
  nextPageToken: string | null;
  apiVersion: string;
  dataLastUpdate: string;
  searchId: string;
  searchDateTime: string;
  userId: string;
  forwardedUserId: string;
}
export interface ZooplaComparableAttributes {
  propertyType?: "Terraced" | "Semi-Detached" | "Detached" | "Flat/Maisonette";
  propertySubType?: string;
  bedrooms?: number;
  receptions?: number;
  bathrooms?: number;
  newBuild?: boolean;
  yearBuilt?: number;
  tenure?: string;
  floorArea?: number;
}

export type LastValueSource = "Land Registry" | "Registers of Scotland" | "Asking Price" | "Valuation";

export interface ZooplaValuationHistoryItem {
  value: number;
  valueType: LastValueType;
  valueDate: string;
  valueSource: LastValueSource;
}
export type ZooplaValuationHistoryItemDTO = Omit<ZooplaValuationHistoryItem, "valueType"> & {
  valueType: LastValueTypeGQL;
};
export interface ZooplaValuation {
  lastValue?: number;
  lastValueType?: LastValueType;
  lastValueDate?: string;
  currentValue?: number;
  currentValuePerSqft?: number;
  history?: ZooplaValuationHistoryItem[];
}

export type ZooplaValuationDTO = Omit<ZooplaValuation, "lastValueType"> & {
  lastValueType: LastValueTypeGQL;
};

export interface ZooplaComparableValuation {
  sale?: ZooplaValuation;
  rent?: ZooplaValuation;
}

export interface ZooplaComparableValuationDTO {
  sale?: ZooplaValuationDTO;
  rent?: ZooplaValuationDTO;
}

export interface ZooplaComparableEnergy {
  currentEnergyRating: string;
  currentEnergyEfficiency: number;
}

export interface ZooplaComparableLocation {
  coordinates: {
    latitude: number;
    longitude: number;
  };
  address: {
    propertyNumberOrName: string;
    streetName: string;
    locality?: string;
    townOrCity: string;
    postcode: string;
    countryCode: string;
  };
  distanceFromSite?: number;
}

export interface NumberRange {
  min?: number;
  max?: number;
}

export interface StringRange {
  min?: string;
  max?: string;
}

export interface ZooplaComparableData {
  uprn: number;
  location: ZooplaComparableLocation;
  attributes: ZooplaComparableAttributes;
  valuation?: ZooplaComparableValuation;
  energy?: ZooplaComparableEnergy;
}

export interface ZooplaComparableByUPRNResponse {
  meta: ZooplaComparableSearchResponseMetaData;
  comparable: ZooplaComparableData;
}

export interface ZooplaListingsByUPRNResponse {
  uprn: string;
  listings: {
    latest: {
      url: string;
    };
  };
}

export interface ZooplaComparableSearchResponse {
  comparables: ZooplaComparableData[];
}

export interface ValuationQuery {
  sale?: {
    currentValue?: { range: NumberRange };
    lastValue?: { range: NumberRange };
    lastValueType?: Array<LastValueType>;
    lastValueDate?: { range: StringRange };
  };
  rent?: {
    lastValue?: { range: NumberRange };
    currentValue?: { range: NumberRange };
    lastValueType: Array<LastValueType>;
  };
}

export type ZooplaTenure = "Freehold" | "Leasehold" | "Feudal" | "Shared";
export interface IZooplaSearchCriteria {
  attributes?: {
    bedrooms?: NumberRange;
    propertyType?: ZooplaPropertyType[];
    tenure?: ZooplaTenure[];
    floorArea?: NumberRange;
    yearBuilt?: NumberRange;
    propertySubType?: ZooplaPropertySubTypeGQL;
    newBuild?: boolean;
  };
  valuation?: ValuationQuery;
}

export type ZooplaComparable = ZooplaComparableData;
export type ZooplaComparableDTO = Omit<ZooplaComparable, "location" | "uprn" | "valuation"> & {
  uprn: string;
  location: Omit<ZooplaComparableLocation, "coordinates"> & {
    coordinates: {
      type: "Point";
      coordinates: [number, number];
    };
  };
  valuation?: ZooplaComparableValuationDTO;
};

export enum LastValueType {
  AskingPrice = "Asking Price",
  Sale = "Sale",
  Valuation = "Valuation",
  Listed = "Listed",
}

export type LastValueTypeGQL = keyof typeof LastValueType;

export type ZooplaComparableConnection = Connection<ZooplaComparableDTO, "ZooplaComparable">;

export enum ZooplaPropertySubType {
  Bungalow_Property = "Bungalow Property",
  Converted_Flat = "Converted Flat",
  Detached_Bungalow = "Detached Bungalow",
  Detached_House = "Detached House",
  Detached_Property = "Detached Property",
  End_Terrace_Bungalow = "End Terrace Bungalow",
  End_Terrace_House = "End Terrace House",
  End_Terrace_Property = "End Terrace Property",
  Flat_Maisonette = "Flat/Maisonette",
  House = "House",
  Mid_Terrace_Bungalow = "Mid Terrace Bungalow",
  Mid_Terrace_House = "Mid Terrace House",
  Mid_Terrace_Property = "Mid Terrace Property",
  Property = "Property",
  Purpose_Built_Flat = "Purpose Built Flat",
  Retirement_Flat = "Retirement Flat",
  Semi_Detached_Bungalow = "Semi-Detached Bungalow",
  Semi_Detached_House = "Semi-Detached House",
  Semi_Detached_Property = "Semi-Detached Property",
  Terrace_Property = "Terrace Property",
  Terraced = "Terraced",
  Terraced_Bungalow = "Terraced Bungalow",
  Town_House = "Town House",
}
export type ZooplaPropertySubTypeGQL = keyof typeof ZooplaPropertySubType;
